import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IContactService } from '@core/services/i-contact.service';
import { ITab } from '@devinforius/i-navigation/lib/models/i-tabsXl';
import { filterItemsByActiveFilters } from '@shared/helpers';
import { Action, ActionsCounts } from '@shared/interfaces/actions';
import { ColumnActiveFilter, ColumnToFilter, DropdownOption } from '@shared/interfaces/dev';
import { BusinessRouter } from '@shared/models/dev';
import { Contact } from '@shared/models/iContact/contact';
import { UserInfo } from '@shared/models/user-info';
import { ActionsService } from '@shared/services/actions.service';
import { ActionScope, ActionType } from '@shared/types/actions';
import { ColumnsFilterEvent, EmbeddedKeys, PaginatedItems } from '@shared/types/dev';
import { PaginatorState } from 'primeng/paginator';
import { catchError, finalize, forkJoin, Observable, of } from 'rxjs';
import { FormOptions } from 'src/app/common/models/form-options';
import { iAdminService } from 'src/app/core/services/iAdmin.service';

@Component({
  selector: 'app-actions',
  templateUrl: './actions.component.html',
  styleUrl: './actions.component.scss',
})
export class ActionsComponent implements OnInit {
  private readonly service = inject(ActionsService);
  private readonly iContactsService = inject(IContactService);

  private readonly businessRouter = inject(BusinessRouter);
  private readonly formOptions = inject(FormOptions);

  public readonly selectedScope = signal<ActionScope>('points');
  public readonly tabs = signal<ITab[]>([
    {
      id: 'to-do',
      label: 'A faire',
    },
    {
      id: 'done',
      label: 'Terminées',
    },
    {
      id: 'all',
      label: 'Toutes',
    },
    {
      id: 'from-me',
      label: "Actions que j'ai demandées",
    },
  ]);
  public readonly userInfos = toSignal<UserInfo>(inject(iAdminService).getUserInfo());
  public readonly currentTab = signal<ActionType>('to-do');
  public readonly currentLimit = signal<number>(20);
  public readonly currentOffset = signal<number>(0);
  public readonly currentPageNumber = computed<number>(() => Math.ceil(this.currentOffset() / this.currentLimit()) + 1);
  public readonly result = signal<PaginatedItems<Action>>({} as PaginatedItems<Action>);
  public readonly actions = computed<Action[]>(() => this.result().items ?? []);

  public readonly fetchingActions = signal<boolean>(false);
  public readonly fetchingActionsCounts = signal<boolean>(false);
  public readonly isActionProcessDialogOpen = signal<boolean>(false);
  public readonly actionToProcess = signal<Action | undefined>(undefined);
  public readonly filterFields = signal<ColumnToFilter<Action>[]>([]);
  private readonly activeFilters = signal<ColumnActiveFilter<Action>[]>([]);
  public readonly contacts = signal<Contact[]>([]);

  public readonly filteredActions = computed<Action[]>(() => {
    const actions = this.actions();

    if (!actions.length) return [];

    const filters = this.activeFilters();
    const filterFields = this.filterFields();

    if (!filters.length || !filterFields.length) return actions;

    return filterItemsByActiveFilters(actions, this.filterFields(), filters);
  });

  ngOnInit(): void {
    this.initOptions();
    this.fetchActionsAndCounts();
  }

  private initOptions(): void {
    this.iContactsService
      .getLowContactsAdmin()
      .pipe(catchError(() => of([] as Contact[])))
      .subscribe({
        next: (contacts) => {
          this.contacts.set(contacts);
          this.fetchActionsAndCounts();
        },
      });
  }

  private fetchActionsAndCounts(): void {
    this.fetchingActions.set(true);
    this.fetchingActionsCounts.set(true);

    forkJoin({
      counts: this.fetchCategoriesCounts(),
      result: this.fetchActions(),
    }).subscribe({
      next: ({ counts, result }) => {
        this.result.set(result);
        this.setCounts(counts);
        this.setFilterFields();
      },
    });
  }

  private setFilterFields(): void {
    this.filterFields.set([
      {
        field: 'purpose',
        fieldLabel: 'Action',
        type: 'enum',
        options: this.formOptions.actionKinds().map((kind) => ({ label: kind.description, value: kind.description })),
      },
      {
        field: 'createdAt',
        fieldLabel: 'Créée le',
        type: 'date',
      },
      {
        field: 'deadline',
        fieldLabel: 'Deadline',
        type: 'date',
      },
      {
        field: 'processable',
        fieldLabel: 'A traiter',
        type: 'boolean',
      },
      // {
      //   field: 'recipient.id' as EmbeddedKeys<Action>,
      //   fieldLabel: 'Destinataire',
      //   type: 'enum',
      //   options: this.getAvailableRecipients()
      // },
    ]);
  }

  private getAvailableRecipients(): DropdownOption[] {
    return [];
    // if (this.globalElements.services_LV.length === 0) {
    //   return [];
    // }
    // const allOptions = this.items
    //   .filter((item) => item.serviceId)
    //   .map((item) => {
    //     const service = this.globalElements.services_LV.find((x) => x.value === item.serviceId);

    //     if (!service) return;

    //     return {
    //       label: service.label,
    //       value: item.serviceId,
    //     };
    //   })
    //   .filter((option) => !!option);

    // if (allOptions.length === 0) {
    //   return [];
    // }

    // return sortBy(uniqBy(allOptions, 'value'), 'label');
  }

  private setCounts(counts: ActionsCounts): void {
    this.tabs.set([
      {
        id: 'to-do',
        label: 'A faire',
        count: counts.toDo,
      },
      {
        id: 'done',
        label: 'Terminées',
        count: counts.done,
      },
      {
        id: 'all',
        label: 'Toutes',
        count: counts.all,
      },
      {
        id: 'from-me',
        label: "Actions que j'ai demandées",
        count: counts.askedByMe,
      },
    ]);
  }

  private fetchCategoriesCounts(): Observable<ActionsCounts> {
    this.fetchingActionsCounts.set(true);

    return this.service.fetchAllCategoriesCounts(this.selectedScope()).pipe(
      catchError(() =>
        of({
          toDo: 0,
          done: 0,
          all: 0,
          askedByMe: 0,
        })
      ),
      finalize(() => this.fetchingActionsCounts.set(false))
    );
  }

  private fetchActions(): Observable<PaginatedItems<Action>> {
    return this.service
      .fetchActions({
        scope: this.selectedScope(),
        type: this.currentTab(),
        query: { PageNumber: this.currentPageNumber(), PageSize: this.currentLimit() },
      })
      .pipe(
        catchError(() =>
          of({
            totalCount: 0,
            totalPages: 0,
            pageNumber: 0,
            pageSize: 0,
            items: [],
          })
        ),
        finalize(() => this.fetchingActions.set(false))
      );
  }

  private resetPagination(): void {
    this.currentOffset.set(0);
    this.currentLimit.set(10);
  }

  public onScopeChange(scope: ActionScope): void {
    this.selectedScope.set(scope);
    this.resetPagination();
    this.emptyFilters();

    this.fetchActionsAndCounts();
  }

  public onTabChange(tab: ITab): void {
    this.currentTab.set(tab.id as ActionType);
    this.resetPagination();

    this.fetchActionsAndCounts();
  }

  private emptyFilters(): void {
    this.activeFilters.set([]);
  }

  public onPageChange(event: PaginatorState): void {
    const { rows, first } = event;

    this.emptyFilters();

    this.currentLimit.set(rows ?? 20);
    this.currentOffset.set(first ?? 0);

    this.fetchActionsAndCounts();
  }

  public onProcessActionClick(action: Action): void {
    this.actionToProcess.set(action);
    this.isActionProcessDialogOpen.set(true);
  }

  public onActionProcessed(): void {
    this.isActionProcessDialogOpen.set(false);
    this.actionToProcess.set(undefined);
    this.fetchActionsAndCounts();
  }

  public onActionClick(action: Action): void {
    if (action.scope !== 'meetings') return;

    const { meetingId } = action;

    if (meetingId < 0) return;

    this.businessRouter.goToAMeeting(meetingId, { newTab: true });
  }

  public goToMeeting(meetingId: number): void {
    if (meetingId < 0) return;

    this.businessRouter.goToAMeeting(meetingId, { newTab: true });
  }

  public goToPoint(pointId: number): void {
    if (pointId < 0) return;

    this.businessRouter.goToAPoint(pointId, { newTab: true });
  }

  public onFilterActions(event: ColumnsFilterEvent<Action>): void {
    this.activeFilters.set(event.filters);
  }

  public onFilterReset(): void {
    this.emptyFilters();
  }
}
